/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2017 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/**
 * @file
 */
/* eslint max-classes-per-file: ["error", 2] */

import uuidv4 from 'uuid/v4';
// eslint-disable-next-line import/no-cycle
import { providers } from './Providers';
import { auth2, IAccessTokenInfo } from './Auth2API';
import { getEnvVar, getHostEnv } from '../core/EnvUtil';
import { logging } from './LoggingAPI';
import { ISearchParams, router } from './RouterAPI';
import { getSingletonFunction } from '../core/ProviderUtil';

const logger = logging.getLogger('FloodgateProvider');

const DEFAULT_API_KEY = 'dc-local-virgoweb';
const IMS_CLIENT_ID_KEY = 'ims_client_id';
const FLOODGATE_URI_KEY = 'floodgate_uri';
let featureFlags = [];
let metaData = {};

const getClientId = () => getEnvVar(IMS_CLIENT_ID_KEY);

export class TimeoutError extends Error {
  constructor(message) {
    super(message);
    this.name = 'TimeoutError';
  }
}

let floodgateImpl;
function getFeatureFlags() {
  return floodgateImpl ? floodgateImpl.getFeatureFlags() : [];
}

function getMetaData() {
  return floodgateImpl ? floodgateImpl.getMetaData() : {};
}

const getFromSessionStorage = key => {
  try {
    if (window.sessionStorage) {
      return window.sessionStorage.getItem(key) || '';
    }
  } catch (e) {
    return '';
  }
};

export interface IFloodgateProviderConfig extends IAccessTokenInfo {
  auth?: any;
  imsClientId?: string;
  context?: Object;
  meta?: boolean;
  floodgateUri?: string;
  useAnonymousUUID?: boolean;
  floodgateResponse?: any;
  cacheConfig?: IFloodgateCachingConfig;
}

export interface IFlagsResult {
  releases: {
    features: string[];
  }[];
}

export interface IMetadataResult {
  releases: {
    meta: {
      k: string;
      v: string;
    }[];
  }[];
}

export interface IFloodgateResponse {
  releases: {
    features: string[];
    meta: {
      k: string;
      v: string;
    }[];
  }[];
  ttl?: number;
  lastUpdatedTimestamp?: number;
}

export enum CacheControl {
  NONE = 'none',
  CLIENT = 'client',
  PROVIDER = 'provider'
}

/**
 * cacheControl = none - No caching
 * cacheControl = client, client will manage caching and will provide getFloodgateResponse and saveFloodgateResponse functions
 * cacheControl = provider, provider will manage caching
 */
export interface IFloodgateCachingConfig {
  cacheControl?: CacheControl;
  getFloodgateResponse?: () => Promise<IFloodgateResponse>;
  saveFloodgateResponse: (response: IFloodgateResponse) => void;
  forceRevalidation?: boolean;
  waitForCacheResponse?: boolean;
}

export interface IFloodgateProvider {
  getFeatureFlags: () => string[];
  getAccessToken: (obj: IAccessTokenInfo) => string;
  ready: () => Promise<any>;
  refreshReady: () => Promise<any>;
  getApiUrl: (baseUri: string) => string;
  apiKey: string;
  getArrayOfFlags: (result?: IFlagsResult) => string[];
  getMetaData: (result?: IMetadataResult) => { [key: string]: string };
  enableMeta: boolean;
  hasFlag: (flagName: string) => boolean;
  hasFlagAsync: (flagName: string, signedInCheck?: boolean) => Promise<boolean>;
  isImsReadySet: () => Promise<boolean>;
  createAnonUserUUID: () => string;
  callFloodgateAPI: () => Promise<any>;
  purgeFloodgateCache: () => void;
}

/**
 * @classdesc
 * Floodgate Provider to manage feature flags
 * @class
 */
class FloodgateProviderImpl implements IFloodgateProvider {
  static getInstance = getSingletonFunction(this);
  auth;
  clientId;
  context;
  enableMeta;
  uri;
  useAnonymousUUID: boolean;
  accessToken;
  timeoutMS;
  readyPromise: Promise<any>;
  clientFloodgateResponse;
  error;
  floodgateAPIWithAuthPromise;
  floodgateAPIWithCachePromise;
  floodgateAPIPromise;
  refreshReadyPromise: Promise<any>;
  cacheConfig;
  enableCaching;
  initialisedWithCachedResponse;

  /**
   * @constructor
   * @param {Object} obj
   * @param {AuthAPI} obj.auth - The instance of AuthAPI for testing.
   * @param {String} obj.floodgateUri - uri to make floodgate API calls
   * @param {String} obj.imsClientId - ims client id registered with floodgate
   * @param {String} obj.imsToken - The instance of AuthAPI for testing.
   * @param {String} obj.useAnonymousUUID - Use userUUID to fetch floodgate flags for anonymous users.
   * @param {Object} obj.context - Key/value pairs to be passed as context to floodgate
   * @param {Boolean} obj.meta - boolean passed to determine if the api should return meta data.
   * @param {Promise} obj.floodgateResponse - Promise that resolves to floodgate response.
   */
  constructor(obj: IFloodgateProviderConfig) {
    try {
      this.auth = obj?.auth || auth2;
      this.clientId = obj?.imsClientId || getClientId();
      this.context = obj?.context || {};
      this.enableMeta = obj?.meta ?? window.adobe_dc_sdk?.launchOptions?.floodgate?.meta ?? false;
      this.uri = this.getApiUrl(obj?.floodgateUri);
      this.useAnonymousUUID = obj?.useAnonymousUUID ?? true;
      this.accessToken = this.getAccessToken(obj);
      this.timeoutMS = 5 * 1000;
      this.auth.signInPromise.then(() => {
        // Invalidate ready. Any calls to this provider from this point on will wait for current floodgate data.
        this.readyPromise = null;

        // Get the latest token
        const newAccessToken = this.getAccessToken(obj);

        logger.emit('Floodgate update', {
          level: 'debug',
          signed_in: auth2.isSignedIn,
          old_auth: this.accessToken && this.accessToken.slice(0, 20),
          new_auth: newAccessToken.slice(0, 20),
        });

        this.accessToken = newAccessToken;

        // Reset providers.floodgate entry so that they can get new readyPromise
        providers.floodgate = () => this.ready();
        // Handle post-sign-in caching and API calls
        this.initialisedWithCachedResponse = false;
        this.floodgateAPIWithCachePromise = null;
      });
      // if client has provided floodgate api response; use that
      this.clientFloodgateResponse = obj?.floodgateResponse || undefined;
      const ccObj = obj?.cacheConfig || window.adobe_dc_sdk?.launchOptions?.fgCacheConfig || {};
      this.cacheConfig = {
        ...ccObj,
        cacheControl: ccObj.cacheControl || CacheControl.NONE,
        forceRevalidation: ccObj.forceRevalidation ?? true,
      };
      this.enableCaching = this.cacheConfig.cacheControl !== CacheControl.NONE;
      this.initialisedWithCachedResponse = false;
      floodgateImpl = this;

      // if caching is enabled, and client has asked for force revalidation revalidate the FG response
      if (this.enableCaching && this.cacheConfig.forceRevalidation) {
        (async () => {
          if (window.adobe_dc_sdk.loaded && window.adobe_dc_sdk.loaded.imsReady) {
            await window.adobe_dc_sdk.loaded.imsReady;
            await this.callFloodgateAPI(true);
          }
        })();
      }
    } catch (e) {
      if (this.clientId) {
        logger.warn('FloodgateProviderImpl constuctor', e);
      }

      // Invalidate ready. Any calls to this provider from this point on will wait for current floodgate data.
      this.readyPromise = null;

      // Reset providers.floodgate entry so that they can get new readyPromise
      providers.floodgate = () => this.ready();
    }
  }

  purgeFloodgateCache(): void {
    if (!this.enableCaching) {
      return;
    }
    // Handle provider-managed caching
    if (this.cacheConfig.cacheControl === CacheControl.PROVIDER) {
      try {
        const userId = this.auth.isSignedIn && this.auth.getUserProfile()?.userId;
        const cacheKey = `fgCache_${this.clientId}${userId ? `_${userId}` : ''}`;
        localStorage.removeItem(cacheKey);
      } catch (e) {
        logger.warn('Error purging floodgate cache', e);
      }
    }
    // Handle client-managed caching
    if (this.cacheConfig.cacheControl === CacheControl.CLIENT
        && this.cacheConfig.saveFloodgateResponse
        && typeof this.cacheConfig.saveFloodgateResponse === 'function') {
      this.cacheConfig.saveFloodgateResponse({});
    }
  }

  private cacheFloodgateResponse(userId?: string, response?: any) {
    if (!this.enableCaching || !response) {
      return;
    }
    try {
      const floodgateResponse: IFloodgateResponse = {
        releases: response?.releases || [],
        ttl: response?.ttl,
        lastUpdatedTimestamp: Date.now(),
      };
      if (this.cacheConfig.cacheControl === CacheControl.PROVIDER) {
        const cacheKey = userId ? `fgCache_${this.clientId}_${userId}` : `fgCache_${this.clientId}`;
        localStorage.setItem(cacheKey, JSON.stringify(floodgateResponse));
      }
      if (this.cacheConfig.cacheControl === CacheControl.CLIENT
        && this.cacheConfig.saveFloodgateResponse
        && typeof this.cacheConfig.saveFloodgateResponse === 'function') {
        this.cacheConfig.saveFloodgateResponse({ ...floodgateResponse, userId });
      }
    } catch (e) {
      logger.error('Error saving data to cache', e);
    }
  }

  /**
   * @description
   * fetch a copy of feature flags.
   * @method
   * @returns {Array} - the array of feature flags
   */
  getFeatureFlags() {
    // return a copy so that our internals are safe
    return featureFlags.slice();
  }

  getAccessToken = (obj: IAccessTokenInfo) => obj?.imsToken || (this.auth.getAccessTokenInfo() || {}).access_token;

  /**
   * @description
   * Standard provider ready() method to allow lazy instantiation of API.
   * @method
   * @returns {Promise} - promise that resolves when FloodgateProvider provider has been instantiated
   */
  ready() {
    if (!this.readyPromise) {
      this.readyPromise = this.callFloodgateAPI()
        .then(result => {
          this.getArrayOfFlags(result);
          this.getMetaData(result);
          if (window.adobe_dc_sdk) {
            window.adobe_dc_sdk.getFeatureFlags = () => getFeatureFlags();
          }
          return this;
        });
    }
    return this.readyPromise;
  }

  /**
   * @description
   * Standard provider refreshReady() method to allow lazy instantiation of API.
   * @method
   * @returns {Promise} - promise that resolves when FloodgateProvider provider has been instantiated
   */
  refreshReady() {
    if (!this.refreshReadyPromise) {
      this.refreshReadyPromise = this.callFloodgateAPI()
        .then(result => {
          this.getArrayOfFlags(result);
          this.getMetaData(result);
          return this;
        });
    }
    return this.refreshReadyPromise;
  }

  /**
   * @method
   * @param {String} baseUri - baseUri for the floodgate API passed from the constructor
   * @returns {String} - Floodgate API uri
   * @public
   */
  getApiUrl(baseUri) {
    const clientId = this.clientId;
    const uri = baseUri || getEnvVar(FLOODGATE_URI_KEY);
    if (!uri || !clientId) {
      throw new Error(`missing ${IMS_CLIENT_ID_KEY} or ${FLOODGATE_URI_KEY}`);
    }

    let contextParams = '';
    Object.keys(this.context).forEach(key => {
      contextParams = `${contextParams}&${key}=${this.context[key]}`;
    });

    return `${uri}/v3/feature?clientId=${clientId}&meta=${this.enableMeta}${contextParams}`;
  }

  /**
   * @method
   * @returns {string} - API key
   * @public
   */
  get apiKey() {
    return this.clientId || DEFAULT_API_KEY;
  }

  /**
   * @method
   * @param {object} result - Object containing various release featureFlags
   * @returns {object} featureFlags - List of feature flags for each release
   * @public
   */
  getArrayOfFlags(result?: IFlagsResult) {
    if (result) {
      featureFlags = [];
      result.releases.forEach(release => {
        release.features.forEach(flag => featureFlags.push(flag));
      });

      const addFlags = getFromSessionStorage('floodgate-add').split(/[, ]+/);
      addFlags.forEach(flag => {
        if (!featureFlags.includes(flag)) {
          featureFlags.push(flag);
        }
      });

      const removeFlags = getFromSessionStorage('floodgate-remove').split(/[, ]+/);
      featureFlags = featureFlags.filter(flag => !removeFlags.includes(flag));
    }
    // return a copy of the feature flags
    return featureFlags.slice();
  }

  /**
   * @method
   * @param {object} result - Object containing various relase featureFlags
   * @returns {object} FeatureFlag metadata
   * @public
   */
  getMetaData(result?: IMetadataResult) {
    if (result && this.enableMeta) {
      metaData = {};
      result.releases.forEach(release => {
        // eslint-disable-next-line no-unused-expressions
        release.meta?.forEach(({ k, v }) => {
          try {
            metaData[k] = window.atob(v);
          } catch (error) {
            logger.error('base64 decryption error:', error);
          }
        });
      });
      try {
        const metaFromSession = getFromSessionStorage('floodgate-meta');
        if (metaFromSession) {
          Object.entries(JSON.parse(metaFromSession)).forEach(([k, v]) => {
            metaData[k] = v;
          });
        }
      } catch (e) {
        logger.warn('error occurred parsing json. error', e);
      }
    }

    return { ...metaData };
  }

  /**
   * @method
   * @param {string} flagName - The flag name whose presence is to be checked.
   * @returns {boolean} - Result of the find operation for given flag
   * @public
   */
  hasFlag(flagName) {
    return featureFlags.includes(flagName);
  }

  /**
   *
   * @param {string} flagName - The flag name whose presence is to be checked.
   * @param {boolean} signedInCheck - The signed in flag boolean.
   * @returns {boolean} - Result a promise with a value of true/false.
   */
  hasFlagAsync = async (flagName, signedInCheck = true) => {
    if (signedInCheck) {
      await this.isImsReadySet();
      await this.refreshReady();
    }
    return featureFlags.includes(flagName);
  }

  /**
   * @returns {boolean} - Result is a value true/false
   */
  isImsReadySet() {
    return window.adobeIMS && window.adobe_dc_sdk.loaded && window.adobe_dc_sdk.loaded.imsReady;
  }

  createAnonUserUUID() {
    const id = `${getHostEnv()}_${this.clientId}_${uuidv4()}`;
    try {
      localStorage.setItem('anonUserUUID', id);
    } catch (e) {
      // squelch if local storage is not available
      logger.warn('Cannot access localStorage', e);
    }
    return id;
  }

  /**
   * Timeout helper function
   * @param {number} ms - Milliseconds to wait before timing out
   * @returns {Promise<never>} - A promise that rejects after the specified timeout
   */
  private timeout(ms: number): Promise<never> {
    return new Promise((_, reject) => setTimeout(() => reject(new TimeoutError('Timeout')), ms));
  }

  /**
   * Get the cached Floodgate response
   * @returns {Promise<any>} - The cached response or null if not available
   */
  private async getCachedFloodgateResponse(): Promise<any> {
    // if FG flags caching handled by client
    if (this.cacheConfig.cacheControl === CacheControl.CLIENT) {
      // as a contract, if getFloodgateResponse is provided client would also register saveFloodgateResponse to get the revalidated response
      if ((!this.cacheConfig.saveFloodgateResponse || typeof this.cacheConfig.saveFloodgateResponse !== 'function')
          && (!this.cacheConfig.getFloodgateResponse || typeof this.cacheConfig.getFloodgateResponse !== 'function')) {
        logger.warn('saveFloodgateResponse is not provided by the client, ignoring getFloodgateResponse');
        return null;
      }
      try {
        const response = await Promise.race([
          this.cacheConfig.getFloodgateResponse(),
          this.timeout(2000), // 2 seconds timeout
        ]);
        if (response && Object.keys(response).length > 0) {
          return response;
        }
      } catch (e) {
        logger.warn('Error fetching clientFloodgateResponse or timeout occurred', e);
      }
    }
    // if FG flags caching handled by provider
    if (this.cacheConfig.cacheControl === CacheControl.PROVIDER) {
      const userId = this.auth.isSignedIn && this.auth.getUserProfile()?.userId;
      const key = `fgCache_${this.clientId}${userId ? `_${userId}` : ''}`;
      const cachedData = localStorage.getItem(key);
      if (cachedData) {
        try {
          const parsedData = JSON.parse(cachedData);
          const currentTime = Date.now();
          if (currentTime > parsedData.lastUpdatedTimestamp + parsedData.ttl * 1000) {
            // Cached response is expired
            localStorage.removeItem(key);
            return null;
          }
          return parsedData;
        } catch (e) {
          logger.warn('Error parsing cached data', e);
        }
      }
    }
    return null;
  }

  /**
   * @method
   * @public
   */
  async callFloodgateAPI(forceRefresh = false) {
    // backward compatibility for clientManagedCaching
    if (this.clientFloodgateResponse) {
      return Promise.resolve(this.clientFloodgateResponse);
    }
    // if forceRefresh is true, make the API call
    if (forceRefresh) {
      return this.makeFloodgateAPICall();
    }
    if (this.enableCaching && !this.initialisedWithCachedResponse) {
      if (this.floodgateAPIWithCachePromise) {
        return this.floodgateAPIWithCachePromise;
      }
      this.floodgateAPIWithCachePromise = (async () => {
        const cachedResponse = await this.getCachedFloodgateResponse();
        if (cachedResponse) {
          this.initialisedWithCachedResponse = true;
          return cachedResponse;
        }
      })();
      // if waitForCacheResponse is true, wait for cache response before making the API call in parallel
      if (this.cacheConfig.waitForCacheResponse) {
        const cachedRes = await this.floodgateAPIWithCachePromise;
        if (cachedRes) {
          return cachedRes;
        }
      }
    }
    const apiCallPromise = this.makeFloodgateAPICall();
    if (!this.floodgateAPIWithCachePromise) {
      return apiCallPromise;
    }
    return Promise.race([
      apiCallPromise.then(apiResponse => ({ source: 'api', response: apiResponse })),
      this.floodgateAPIWithCachePromise.then(cacheResponse => ({ source: 'cache', response: cacheResponse })),
    ]).then(result => {
      // if cache response is null or undefined, return the api call promise
      if (result.source === 'cache' && !result.response) {
        return apiCallPromise;
      }
      return result.response;
    });
  }

  private makeFloodgateAPICall() {
    const authentication = this.accessToken ? `Bearer ${this.accessToken}` : '';

    // Ensure we do not call floodgate again unnecessary
    // Use appropriate promise as per authentication status
    if (authentication && this.floodgateAPIWithAuthPromise) {
      return this.floodgateAPIWithAuthPromise;
    }
    if (!authentication && this.floodgateAPIPromise) {
      return this.floodgateAPIPromise;
    }
    const fetchHeaders: HeadersInit = {
      'x-api-key': this.apiKey,
    };
      // Always use the uuid for ensuring stickiness in case of unath, or unath -> auth scenario
    if (this.useAnonymousUUID) {
      const queryParams: ISearchParams = router.getQueryParams();
      // The router will have query param when coming from other origins like acrobat.com
      try {
        fetchHeaders['x-adobe-uuid'] = queryParams.anonUserUUID || localStorage.getItem('anonUserUUID');
      } catch (e) {
        // squelch if local storage is not available
        logger.warn('Cannot access localStorage', e);
      }
      if (!fetchHeaders['x-adobe-uuid']) {
        fetchHeaders['x-adobe-uuid'] = this.createAnonUserUUID();
      }
    }
    let userIdAtCallTime = null;
    if (authentication) {
      fetchHeaders.Authorization = authentication;
      userIdAtCallTime = this.auth.getUserProfile()?.userId;
    }

    fetchHeaders['x-gw-ims-org-id'] = '9E1005A551ED61CA0A490D45@AdobeOrg';
    fetchHeaders['x-sandbox-name'] = getHostEnv() === 'prod' ? 'xlg-prod' : 'xlg-stage';

    const fetchPromise = fetch(this.uri, {
      method: 'GET',
      headers: fetchHeaders,
    });

    let timeoutId;
    const fetchTimeout = new Promise((_resolve, reject) => {
      timeoutId = setTimeout(() => reject(new TimeoutError(`request timed out after ${this.timeoutMS} ms`)), this.timeoutMS);
    });

    let res: Response;
    let resolveFloodgate;
    const p = new Promise(resolve => {
      resolveFloodgate = resolve;
    });
    if (authentication) {
      this.floodgateAPIWithAuthPromise = p;
    } else {
      this.floodgateAPIPromise = p;
    }
    Promise.race([fetchPromise, fetchTimeout]).then((response: Awaited<Response>) => {
      res = response;
      const contentType = response.headers.get('content-type');
      if (response.ok && contentType && contentType.includes('application/json')) {
        // reset the error, just in case someone reset the readyPromise
        this.error = undefined;
        return response.json();
      }
      // We need to deference the text() Promise to get any error detail.
      return response.text().then(result => {
        const text = `Floodgate API failed with status ${
          response.status
        } ${
          response.statusText
        } type ${
          contentType
        } text ${
          result}`;
        throw new TypeError(text);
      });
    }).then(result => {
      // would be better if this were a finally block, except for IE...
      clearTimeout(timeoutId);
      resolveFloodgate(result);
      this.cacheFloodgateResponse(userIdAtCallTime, result);
      return result;
    }).catch(err => {
      clearTimeout(timeoutId);
      const errorInfo = {
        auth: authentication.slice(0, 20),
        err,
        level: 'warn',
      };
      if (err instanceof TimeoutError) {
        logger.emit('Floodgate timeout', {
          ...errorInfo,
        });
      } else {
        logger.emit('Floodgate failure', {
          ...errorInfo,
          code: res?.status,
          status_text: res?.statusText,
        });
      }
      this.error = err.message ? err : new Error(`Floodgate failure: ${JSON.stringify(err)}`);
      resolveFloodgate(undefined);
    });
    return p;
  }
}

export { getFeatureFlags, getMetaData };
export default FloodgateProviderImpl;
